import { throwError as observableThrowError, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { catchError, timeout } from 'rxjs/operators';

import {
  HttpClient,
  HttpHandler,
  HttpParams,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';

import { GlobalObservables } from '../../../app.global.observer';
import { Router } from '@angular/router';

declare global {
  interface Date {
    toJSON(): string;
  }
}

export interface IRequestOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

export interface IRequestOptionsBlob {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType: 'blob';
  withCredentials?: boolean;
}

@Injectable()
export class HttpService extends HttpClient {
  globalObs;

  // Extending the HttpClient through the Angular DI.
  constructor(
    handler: HttpHandler,
    globalObs: GlobalObservables,
    private router: Router
  ) {
    super(handler);
    this.globalObs = globalObs;
  }

  getAccessToken(): string {
    let rmToken = '';
    if (localStorage.getItem('rm_access_token')) {
      rmToken = localStorage.getItem('rm_access_token');
    }
    return rmToken;
  }

  /**
   * ANY request
   * @param {string} method
   * @param {string} url
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Object>}
   */
  Request(method: string, url: string, options?: IRequestOptions): Observable<object> {
    return super.request(method, url, options).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          (error.status === 401 || error.status === 403) &&
          (window.location.href.match(/\?/g) || []).length < 2 &&
          window.location.pathname !== '/login'
        ) {
          const location = window.location.origin + '/login';
          window.location.href = location;
        } else if (error.status === 500 || error.status === 502) {
          this.globalObs.pushError.next({
            status: error.status,
            statusText: error.statusText,
            message: '',
            url: error.url,
            date: new Date(),
          });
        }
        return observableThrowError(error);
      })
    );
  }

  /**
   * GET request
   * @param {string} url
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Object>}
   */
  Get(url: string, options?: IRequestOptions): Observable<object> {
    options = options || {};

    // let rmAccessToken = '';
    // if (localStorage.getItem('rm_access_token')) {
    //   rmAccessToken = localStorage.getItem('rm_access_token');
    // }

    const rmAccessToken = this.getAccessToken();

    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + rmAccessToken,
    });
    options.headers = headers;

    url += (url.indexOf('?') > 0 ? '&' : '?') + 'timestamp=' + new Date().getTime().toString();

    return super.get(url, options).pipe(
      timeout(30000),
      catchError((error: HttpErrorResponse) => {
        if (
          (error.status === 401 || error.status === 403) &&
          (window.location.href.match(/\?/g) || []).length < 2 &&
          window.location.pathname !== '/login'
        ) {
          const location = window.location.origin + '/login';
          window.location.href = location;
        } else if (error.status === 500 || error.status === 502) {
          this.globalObs.pushError.next({
            status: error.status,
            statusText: error.statusText,
            message: '',
            url: error.url,
            date: new Date(),
          });

          if (error !== null && error.error != null && error.error.RequiresLogin) {
            document.cookie = 'rm4= ; expires = Thu, 01 Jan 1970 00:00:00 GMT';
            this.router.navigate(['/login']);
          }
        }

        return observableThrowError(error);
      })
    );
  }

  /**
   * Unsecured GET request
   * @param {string} url
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Object>}
   */
  UnsecuredGet(url: string, options?: IRequestOptions): Observable<object> {
    options = options || {};
    url += (url.indexOf('?') > 0 ? '&' : '?') + 'timestamp=' + new Date().getTime().toString();
    return super.get(url, options).pipe(
      catchError((error: HttpErrorResponse) => {
        this.globalObs.pushError.next({
          status: error.status,
          statusText: error.statusText,
          message: '',
          url: error.url,
          date: new Date(),
        });
        return observableThrowError(error);
      })
    );
  }

  /**
   * POST request
   * @param {string} url
   * @param {any | null} body
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Object>}
   */
  Post(url: string, body: any | null, options?: IRequestOptions): Observable<object> {
    options = options || {};
    // let rmToken = '';
    // if (localStorage.getItem('rm_access_token')) {
    //   rmToken = localStorage.getItem('rm_access_token');
    // }

    const rmAccessToken = this.getAccessToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + rmAccessToken,
    });
    options.headers = headers;

    return super.post(url, body, options).pipe(
      timeout(30000),
      catchError((error: HttpErrorResponse) => {
        if (
          (error.status === 401 || error.status === 403) &&
          (window.location.href.match(/\?/g) || []).length < 2 &&
          window.location.pathname !== '/login'
        ) {
          const location = window.location.origin + '/login';
          window.location.href = location;
        } else if (error.status === 500 || error.status === 502) {
          this.globalObs.pushError.next({
            status: error.status,
            statusText: error.statusText,
            message: '',
            url: error.url,
            date: new Date(),
          });

          if (error !== null && error.error != null && error.error.RequiresLogin) {
            document.cookie = 'rm4= ; expires = Thu, 01 Jan 1970 00:00:00 GMT';
            this.router.navigate(['/login']);
          }
        }
        return observableThrowError(error);
      })
    );
  }

  /**
   * POST Blob request
   * @param {string} url
   * @param {any | null} body
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Blob>}
   */
  PostBlob(url: string, body: any | null, options?: IRequestOptionsBlob): Observable<Blob> {
    options = options || {
      responseType: 'blob',
    };
    // let rmToken = '';
    // if (localStorage.getItem('rm_access_token')) {
    //   rmToken = localStorage.getItem('rm_access_token');
    // }

    const rmAccessToken = this.getAccessToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + rmAccessToken,
    });
    options.headers = headers;

    return super.post(url, body, options).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          (error.status === 401 || error.status === 403) &&
          (window.location.href.match(/\?/g) || []).length < 2 &&
          window.location.pathname !== '/login'
        ) {
          const location = window.location.origin + '/login';
          window.location.href = location;
        } else if (error.status === 500 || error.status === 502) {
          this.globalObs.pushError.next({
            status: error.status,
            statusText: error.statusText,
            message: '',
            url: error.url,
            date: new Date(),
          });

          if (error !== null && error.error != null && error.error.RequiresLogin) {
            document.cookie = 'rm4= ; expires = Thu, 01 Jan 1970 00:00:00 GMT';
            this.router.navigate(['/login']);
          }
        }
        return observableThrowError(error);
      })
    );
  }

  /**
   * Unsecured POST request
   * @param {string} url
   * @param {any | null} body
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Object>}
   */
  UnsecuredPost(url: string, body: any | null, options?: IRequestOptions): Observable<object> {
    options = options || {};
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    options.headers = headers;
    return super.post(url, body, options).pipe(
      catchError((error: HttpErrorResponse) => {
        this.globalObs.pushError.next({
          status: error.status,
          statusText: error.statusText,
          message: '',
          url: error.url,
          date: new Date(),
        });
        return observableThrowError(error);
      })
    );
  }

  /**
   * Unsecured POST request
   * @param {string} url
   * @param {IRequestOptions} options options of the request like headers, body, etc.
   * @returns {Observable<Object>}
   */
  Delete(url: string, options?: IRequestOptions): Observable<object> {
    options = options || {};
    /// / APPEND JWT TOKEN TO REQUEST
    // let rmAccessToken = '';
    // if (localStorage.getItem('rm_access_token')) {
    //   rmAccessToken = localStorage.getItem('rm_access_token');
    // }

    const rmAccessToken = this.getAccessToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + rmAccessToken,
    });
    options.headers = headers;

    return super.delete(url, options).pipe(
      catchError((error: HttpErrorResponse) => {
        this.globalObs.pushError.next({
          status: error.status,
          statusText: error.statusText,
          message: '',
          url: error.url,
          date: new Date(),
        });
        return observableThrowError(error);
      })
    );
  }
}
